import { AnyObject } from "../../../global";
import FieldRenderType from "./enums/FieldRenderType";
import { FormDefaultValueType, FormValueType, SchemaField } from "./types/SchemaField";
import { ConflictSuffix } from "./enums/Conflict";
import FieldFetchType from "./enums/FieldFetchType";
import _ from "lodash";
import { FormFieldOption } from "../fields/types";
import { FormValueSaveType } from "./types/FormValueSaveType";
import { FormValueApiReqType } from "./types/FormValueApiReqType";
import { isEmpty } from "shared/utils";
import { DISPLAY_ORDER_KEY } from "shared/utils/constants";
import { DecisionEvaluator } from "./DecisionEvaluator";
import { StaticDataObject } from "../../../context/staticData/staticData";
import FieldOption from "./types/FieldOption";
import { ApiFieldAsset } from "shared/types/ApiFieldAsset";

/**
 *  @class SchemaService
 *  @description This class includes some utility methods used by Dynamic form renderer.
 */
export default class SchemaService {
  /**
   * @description This method will return initial values required by an Form.
   * @param schemaList
   */
  static getInitialValue(schemaList: Array<SchemaField>) {
    return schemaList.reduce((result, schemaObj) => {
      return {
        ...result,
        ...this.getFieldInitialValueMap(schemaObj)
      };
    }, {});
  }

  /**
   * Will return intial value for a field based on renderType
   * @param schemaObj
   */
  static getFieldInitialValue(schemaObj: SchemaField) {
    switch (schemaObj.renderType) {
      case FieldRenderType.CHECK_BOX:
        return SchemaService.getFieldValue(schemaObj, false);
      case FieldRenderType.MULTI_SELECT:
      case FieldRenderType.DIVISION:
      case FieldRenderType.NESTED_MULTI_SELECT:
      case FieldRenderType.MULTI_IMAGE:
      case FieldRenderType.MULTI_FILE:
      case FieldRenderType.PRIVATE_MULTI_FILE:
      case FieldRenderType.TAGS:
      case FieldRenderType.EMAIL_LIST:
        return SchemaService.getFieldValue(schemaObj, []);
      case FieldRenderType.HTML:
      case FieldRenderType.SECTION_SEPARATOR:
        return undefined;
      default:
        return SchemaService.getFieldValue(schemaObj, "");
    }
  }

  /**
   * Will return value alongwith conflict values if any
   * @param schemaObj
   * @type SchemaField
   * @private
   */
  private static getFieldInitialValueMap(schemaObj: SchemaField): AnyObject {
    let valueMap: AnyObject = { [schemaObj.id]: SchemaService.getFieldInitialValue(schemaObj) };
    if (schemaObj.conflictValue !== null) {
      valueMap = {
        ...valueMap,
        [`${schemaObj.id}${ConflictSuffix.VALUE}`]: schemaObj.conflictValue,
        [`${schemaObj.id}${ConflictSuffix.CHECKBOX}`]: false
      };
    }
    return valueMap;
  }

  /**
   * When form submit handler is called then this method will parse the values based on field type
   * @param schemaList
   * @param formValues
   */
  static getValuesOnFormSubmit(schemaList: Array<SchemaField>, formValues: AnyObject): Array<FormValueSaveType> {
    return [...schemaList].map(schemaObj => {
      const value = this.getFieldValueOnSubmit(schemaObj, formValues, schemaList);
      return {
        id: schemaObj.id,
        alias: schemaObj.alias,
        system: schemaObj.system,
        value: value,
        uiOnlyField: schemaObj.uiOnlyField
      };
    });
  }

  /**
   * Will parse value based on FieldType
   * @param schemaObj
   * @param formValues
   * @param schemaList
   * @type SchemaField
   * @private
   */
  private static getFieldValueOnSubmit(schemaObj: SchemaField, formValues: AnyObject, schemaList: Array<SchemaField>) {
    const fieldHidden = !DecisionEvaluator.isFieldVisible({
      formValues,
      schemaObj,
      schemaList
    });

    if (fieldHidden) {
      return this.getDefaultValueForHiddenFieldType(schemaObj.renderType, schemaObj.defaultValue);
    }
    const value = formValues[`${schemaObj.id}${ConflictSuffix.CHECKBOX}`]
      ? formValues[`${schemaObj.id}${ConflictSuffix.VALUE}`]
      : formValues[schemaObj.id];

    switch (schemaObj.renderType) {
      case FieldRenderType.MULTI_LINE_TEXTFIELD:
        return value.trim();
      case FieldRenderType.MONEY:
        return isNaN(parseFloat(value)) ? null : parseFloat(value);
      case FieldRenderType.INTEGER:
        return isNaN(parseInt(value)) ? null : parseInt(value);
      case FieldRenderType.SINGLE_IMAGE:
      case FieldRenderType.SINGLE_IMAGE_WITH_ASPECT_RATIO:
        return this.getSingleImageFieldValueOnSubmit(value);
      case FieldRenderType.MULTI_IMAGE:
        return this.getMultiImageFieldValueOnSubmit(value);
      case FieldRenderType.EMAIL:
        return value.toLowerCase();
      default:
        return value;
    }
  }

  static getSingleImageFieldValueOnSubmit(value: ApiFieldAsset) {
    if (value) {
      //warning flag is added in asset, removing before sending it to api.
      const { incorrectAspectRatio, ...rest } = value;
      return rest;
    }
    return value;
  }

  static getMultiImageFieldValueOnSubmit(value: Array<ApiFieldAsset>) {
    if (Array.isArray(value)) {
      return value.map((item: ApiFieldAsset) => {
        return this.getSingleImageFieldValueOnSubmit(item);
      });
    }
    return value;
  }

  private static getDefaultValueForHiddenFieldType(renderType: FieldRenderType, defaultValue?: FormDefaultValueType) {
    if (defaultValue !== null && defaultValue !== undefined) {
      switch (renderType) {
        case FieldRenderType.PHONE:
          return "";
        default:
          return defaultValue;
      }
    }
    switch (renderType) {
      case FieldRenderType.CHECK_BOX:
      case FieldRenderType.MULTI_SELECT:
      case FieldRenderType.NESTED_MULTI_SELECT:
      case FieldRenderType.MULTI_IMAGE:
      case FieldRenderType.MULTI_FILE:
      case FieldRenderType.PRIVATE_MULTI_FILE:
      case FieldRenderType.TAGS:
      case FieldRenderType.DIVISION:
        return null;
      default:
        // for all other field types, the value will be string type
        return "";
    }
  }

  static parseOptions({
    staticData,
    ...props
  }: {
    schemaObj: SchemaField;
    staticData: StaticDataObject;
  }): Array<FormFieldOption> {
    switch (props.schemaObj.fetchType) {
      case FieldFetchType.OPTION:
        return _.sortBy(props.schemaObj.options, DISPLAY_ORDER_KEY).map(item => {
          return {
            label: item.value,
            value: item.id,
            customData: item.customData,
            childOptions: item.childOptions
              ? this.convertToFormFieldOption({ options: item.childOptions })
              : item.childOptions,
            imageIcon: item.imageIcon
          };
        });
      case FieldFetchType.STATIC:
        const options = _.get(staticData.staticOptions, props.schemaObj.fetchStatic || "") || [];
        return this.convertToFormFieldOption({ options });
      default:
        return [];
    }
  }

  static convertToFormFieldOption({ options }: { options: Array<FieldOption> }): Array<FormFieldOption> {
    return _.sortBy(options, DISPLAY_ORDER_KEY).map(item => {
      return {
        label: item.value,
        value: item.id
      };
    });
  }

  static getOptionsListFromStaticData(staticOptionMap?: AnyObject): Array<FormFieldOption> {
    if (!staticOptionMap) {
      return [];
    }
    return _.sortBy(
      Object.keys(staticOptionMap).map((key, index) => {
        return {
          label: staticOptionMap[key],
          value: key
        };
      }),
      "label"
    );
  }

  static getOptionsListFromStaticDataWithCustomLabels({
    staticOptionMap,
    customLabelHandler
  }: {
    staticOptionMap?: AnyObject;
    customLabelHandler: Function;
  }) {
    if (!staticOptionMap) {
      return [];
    }
    return _.sortBy(
      Object.keys(staticOptionMap).map((key, index) => {
        return {
          label: customLabelHandler({ key, value: staticOptionMap[key] }),
          value: key,
          labelToSortWith: staticOptionMap[key]
        };
      }),
      obj => obj.labelToSortWith.toLowerCase()
    ).map(o => ({ label: o.label, value: o.value }));
  }

  static getOptionsListFromStaticDataMap({
    selectedMapKey,
    staticDataMap,
    staticDataOptions
  }: {
    selectedMapKey?: string;
    staticDataMap: AnyObject;
    staticDataOptions: AnyObject;
  }): Array<FormFieldOption> {
    if (!selectedMapKey) return [];
    const options = staticDataMap[selectedMapKey];
    if (!options) return [];
    return _.sortBy(
      options.map((key: string) => {
        return {
          label: staticDataOptions[key],
          value: key
        };
      }),
      "label"
    );
  }

  static createApiReqObjForSubmit(formValues: Array<FormValueSaveType>): FormValueApiReqType {
    const systemFieldValues = formValues.reduce((prevVal, currVal) => {
      if (!currVal.system || !currVal.alias) {
        return { ...prevVal };
      }
      return { ...prevVal, [currVal.alias]: currVal.value };
    }, {});
    /**
     * System field are sent on top level and profile map both (for Show Decision Evaluation)
     * At some places we have defined custom field on frontend which shouldn't be send in profile map.
     */
    return {
      ...systemFieldValues,
      profile: formValues.filter(item => !item.uiOnlyField)
    };
  }

  private static getFieldValue(schemaObj: SchemaField, placeholderValue: FormValueType) {
    if (!isEmpty(schemaObj.value)) {
      return schemaObj.value;
    } else if (!isEmpty(schemaObj.defaultValue)) {
      switch (schemaObj.renderType) {
        case FieldRenderType.PHONE:
          return "";
        default:
          return schemaObj.defaultValue;
      }
    } else {
      return placeholderValue;
    }
  }
}
